import { dehydrate, QueryClient } from 'react-query';
import { GetStaticProps } from 'next';
import dynamic from 'next/dynamic';

import MetaTags from 'src/components/HeadMeta';
import global from 'src/constants/global';
import { queryKeyBannerList, queryKeyGameList } from 'src/hooks';
import { wrapper } from 'src/redux/store';
import { fetchAPI } from 'src/utils/fetch-api';

const Games = dynamic(() => import('src/containers/Games'));

const GamesPage = () => {
	const meta = {
		title: 'Discover the Best WEB3 Games on CreoPlay',
		description:
			'Browse and play top WEB3 games on CreoPlay. Earn rewards and explore a world of blockchain gaming.',
		url: '/games',
		schema: JSON.stringify({
			'@context': 'https://schema.org',
			'@type': 'WebPage',
			name: 'CreoPlay Games',
			url: 'https://creoplay.app/games',
			description:
				'Explore a variety of Web3 games on CreoPlay, the blockchain gaming hub for developers and gamers.',
			breadcrumb: {
				'@type': 'BreadcrumbList',
				itemListElement: [
					{
						'@type': 'ListItem',
						position: 1,
						name: 'Home',
						item: 'https://creoplay.app/',
					},
					{
						'@type': 'ListItem',
						position: 2,
						name: 'Games',
						item: 'https://creoplay.app/games',
					},
				],
			},
			hasPart: {
				'@type': 'ItemList',
				name: 'CreoPlay Game List',
				description: 'A collection of Web3 games available on CreoPlay.',
				itemListElement: [],
			},
		}),
	};

	return (
		<>
			<MetaTags {...meta} />
			<Games />
		</>
	);
};

export const getStaticProps: GetStaticProps = wrapper.getStaticProps(() => async () => {
	const queryClient = new QueryClient();

	let notFound = false;

	try {
		const paramsBL: any = { type: 'games' };
		await queryClient.prefetchQuery([queryKeyBannerList, paramsBL], () =>
			fetchAPI({
				endpoint: '/home/banners',
				params: paramsBL,
			}),
		);

		const paramsGL: any = {
			sort: 'name',
			sortBy: 'asc',
			page: 1,
			limit: 8,
		};
		await queryClient.prefetchQuery([queryKeyGameList, paramsGL], () =>
			fetchAPI({
				endpoint: '/home/games',
				params: paramsGL,
			}),
		);

		await queryClient.prefetchQuery(['chains'], () =>
			fetchAPI({
				endpoint: '/master/chains',
			}),
		);

		await queryClient.prefetchQuery(['platforms'], () =>
			fetchAPI({
				endpoint: '/master/platforms',
			}),
		);

		await queryClient.prefetchQuery(['tags'], () =>
			fetchAPI({
				endpoint: '/master/tags',
			}),
		);

		await queryClient.prefetchQuery(['game-types'], () =>
			fetchAPI({
				endpoint: '/master/game-types',
			}),
		);

		await queryClient.prefetchQuery(['game-release-status'], () =>
			fetchAPI({
				endpoint: '/master/game-release-status',
			}),
		);
	} catch (err: any) {
		if (err?.status === 400) {
			return {
				notFound: true,
			};
		} else {
			throw err;
		}
	}

	if (notFound) {
		return {
			notFound: true,
		};
	}

	return {
		props: {
			dehydratedState: dehydrate(queryClient),
		},
		revalidate: global.REVALIDATE_EXP,
	};
});

export default GamesPage;
